import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { Directive, effect, input } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { CriteriaObj } from '@src/suite/common/data-models/criteria/criteria-obj';
import { CUSTOM_MAT_ICONS } from '@src/suite/common/enums/constants';
import { CustomMatIcon } from '@src/suite/common/enums/enums';
import { getContrastYIQ } from '@src/suite/common/utils/util';

import { AbstractAdornerDirective } from './abstract-adorner';

@Directive({
    selector: '[st-assignment-icon]',
})
export class AssignmentIconDirective extends AbstractAdornerDirective {
    private oldToNewIconsMap = {
        'icon-Graphic': 'bar_chart',
        'icon-Interactive': 'ads_click',
        'icon-Audio': 'volume_up',
        'icon-LiveVideo': 'live_tv',
        'icon-Text': 'history_edu',
        'icon-Photo': 'image',
        'icon-Video': 'videocam',
        'icon-SocialPromotion': 'public',
    };

    hasBorder = input(false);
    hasValue = input(false);
    addIcon = input(true, { transform: coerceBooleanProperty });

    constructor() {
        super();
        effect(() => {
            if (this.hasBorder() || this.hasValue()) {
                this.applyAdorner();
            } else {
                this.applyAdorner();
            }
        });
    }

    private lastMatIconEl: any;
    protected get locationID(): string {
        return this.globalTablesService.adornersIDS.AssignmentIcon;
    }

    protected getAdornerIcon(adorner: Adorner): string {
        return this.oldToNewIconsMap[adorner.Icon] || adorner.Icon;
    }

    protected applyVC(vcToApply: { visibilityCriteria: CriteriaObj; adorner: Adorner }) {
        if (vcToApply && vcToApply.adorner) {
            let isCustomMatIcon = false;

            if (vcToApply.adorner.Icon && this.addIcon()) {
                const icon = this.getAdornerIcon(vcToApply.adorner);
                isCustomMatIcon = CUSTOM_MAT_ICONS.includes(icon as CustomMatIcon);
                if (this.lastMatIconEl) {
                    this.renderer.removeChild(this.el.nativeElement, this.lastMatIconEl.location.nativeElement);
                    this.lastMatIconEl.destroy();
                    this.lastMatIconEl = null;
                }
                this.lastMatIconEl = this.viewContainerRef.createComponent(MatIcon);
                this.renderer.addClass(this.lastMatIconEl.location.nativeElement, 'st-icon-content');
                if (isCustomMatIcon) {
                    this.lastMatIconEl.instance.svgIcon = icon;
                } else {
                    const matIconText = this.renderer.createText(icon);
                    this.renderer.appendChild(this.lastMatIconEl.location.nativeElement, matIconText);
                }

                this.renderer.appendChild(this.el.nativeElement, this.lastMatIconEl.location.nativeElement);
            } else if (vcToApply.adorner.IconUrl && this.addIcon()) {
                this.renderer.setStyle(this.el.nativeElement, 'background-image', `url(${vcToApply.adorner.IconUrl})`);
                this.renderer.setStyle(this.el.nativeElement, 'background-size', 'contain');
            }

            if (vcToApply.adorner.Color) {
                if (this.lastMatIconEl) {
                    if (isCustomMatIcon) {
                        this.renderer.setStyle(this.lastMatIconEl.location.nativeElement, 'fill', this.hasBorder() && this.hasValue() ? '#ffffff' : vcToApply.adorner.Color);
                        this.renderer.setStyle(this.lastMatIconEl.location.nativeElement, 'color', this.hasBorder() && this.hasValue() ? '#ffffff' : vcToApply.adorner.Color);
                    } else {
                        this.renderer.setStyle(this.lastMatIconEl.location.nativeElement, 'color', this.hasBorder() && this.hasValue() ? '#ffffff' : vcToApply.adorner.Color);
                    }
                }

                if (this.hasBorder()) {
                    this.renderer.setStyle(this.el.nativeElement, 'border', '0.125rem solid');
                    if (this.hasValue()) {
                        this.renderer.setStyle(this.el.nativeElement, 'background', vcToApply.adorner.Color);
                    } else {
                        this.renderer.removeStyle(this.el.nativeElement, 'background');
                    }
                    this.renderer.setStyle(this.el.nativeElement, 'border-color', vcToApply.adorner.Color);
                } else {
                    if ((vcToApply.adorner.Icon || vcToApply.adorner.IconUrl) && this.addIcon()) {
                        this.renderer.removeStyle(this.el.nativeElement, 'border');
                        this.renderer.removeStyle(this.el.nativeElement, 'border-color');
                        this.renderer.removeStyle(this.el.nativeElement, 'background');
                    } else {
                        this.renderer.setStyle(this.el.nativeElement, 'color', getContrastYIQ(vcToApply.adorner.Color));
                        this.renderer.setStyle(this.el.nativeElement, 'background', vcToApply.adorner.Color);
                    }
                }
            } else {
                this.renderer.removeStyle(this.el.nativeElement, 'border-color');
                this.renderer.removeStyle(this.el.nativeElement, 'background');
            }
        } else {
            this.fallbackToDefault(this.itemType());
        }
    }

    private fallbackToDefault(type: string) {
        if (type) {
            const predefined = this.predefinedIconsMap[type.toUpperCase()];
            if (predefined) {
                if (this.lastMatIconEl) {
                    this.renderer.removeChild(this.el.nativeElement, this.lastMatIconEl.location.nativeElement);
                    this.lastMatIconEl.destroy();
                    this.lastMatIconEl = null;
                }
                const isCustomMatIcon = CUSTOM_MAT_ICONS.includes(predefined.icon as CustomMatIcon);
                this.lastMatIconEl = this.viewContainerRef.createComponent(MatIcon);
                this.renderer.addClass(this.lastMatIconEl.location.nativeElement, 'st-icon-content');
                if (isCustomMatIcon) {
                    this.lastMatIconEl.instance.svgIcon = predefined.icon;
                    this.renderer.setStyle(this.lastMatIconEl.location.nativeElement, 'fill', this.hasBorder() && this.hasValue() ? '#ffffff' : predefined.color);
                    this.renderer.setStyle(this.lastMatIconEl.location.nativeElement, 'color', this.hasBorder() && this.hasValue() ? '#ffffff' : predefined.color);
                } else {
                    const matIconText = this.renderer.createText(predefined.icon);
                    this.renderer.setStyle(this.lastMatIconEl.location.nativeElement, 'color', this.hasBorder() && this.hasValue() ? '#ffffff' : predefined.color);
                    this.renderer.appendChild(this.lastMatIconEl.location.nativeElement, matIconText);
                }

                this.renderer.appendChild(this.el.nativeElement, this.lastMatIconEl.location.nativeElement);

                if (this.hasBorder()) {
                    this.renderer.setStyle(this.el.nativeElement, 'border', '0.125rem solid');
                    if (this.hasValue()) {
                        this.renderer.setStyle(this.el.nativeElement, 'background', predefined.color);
                    } else {
                        this.renderer.removeStyle(this.el.nativeElement, 'background');
                    }
                    this.renderer.setStyle(this.el.nativeElement, 'border-color', predefined.color);
                } else {
                    this.renderer.removeStyle(this.el.nativeElement, 'border');
                    this.renderer.removeStyle(this.el.nativeElement, 'border-color');
                    this.renderer.removeStyle(this.el.nativeElement, 'background');
                }
            }
        }
    }
}
